import React from "react";
// UI Framework
import { toast } from "react-toastify";
// Hooks
import { useAppSelector } from "src/App/hooks";
// Common components
import { Button, TextInput, FullScreenLoading } from "src/Components";
import SelectableButtons from "src/Components/Dropdown/SelectableButtons";
// Requests
import {
  copyFixedBasePriceOpeningSettings,
  setPairsConfig,
  getPairsConfig,
} from "src/Requests/coveredCall";
// Utilities
import { packetErrorHandler } from "src/Utils/requestUtils";
// Types
import type { IPairConfig, IPairConfigDto } from "src/Interfaces/coveredCall";

interface IProps {
  onClose: () => void;
  useFixedBasePrices?: boolean;
  initialRiskProfileId?: string;
  onCopied?: () => void;
}
const OpenningSettingsModal: React.FC<IProps> = ({
  onClose,
  useFixedBasePrices = false,
  initialRiskProfileId,
  onCopied,
}) => {
  // store
  const riskProfiles = useAppSelector(
    (store) => store.coveredCall.riskProfiles,
  );
  // local store
  const [loading, setLoading] = React.useState(false);
  const [copying, setCopying] = React.useState(false);
  const [riskProfileId, setRiskProfileIds] = React.useState<string>(
    initialRiskProfileId ?? riskProfiles[0]?.id?.toString() ?? "0",
  );

  const [config, setConfig] = React.useState({
    baseInstruments: [] as IPairConfig[],
  } as IPairConfigDto);

  React.useEffect(() => {
    getPairsConfig(riskProfileId, useFixedBasePrices)
      .then((resp) => {
        setConfig(resp);
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  }, [riskProfileId, useFixedBasePrices]);

  const setConfigHandler = () => {
    setLoading(true);
    setPairsConfig(config, riskProfileId, useFixedBasePrices)
      .then((resp) => {
        if (resp.data) {
          toast.success(resp.msg);
          onClose();
        } else toast.error(resp.msg);
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  const copyFromEntryPosition = () => {
    const numericRiskProfileId = Number(riskProfileId);
    if (
      riskProfileId == null ||
      riskProfileId === "" ||
      !Number.isInteger(numericRiskProfileId) ||
      numericRiskProfileId < 0
    ) {
      toast.error("ریسک پروفایل انتخاب نشده است");
      return;
    }

    setCopying(true);
    copyFixedBasePriceOpeningSettings(riskProfileId)
      .then(async (resp) => {
        if (!resp.data) {
          toast.error(resp.msg);
          return;
        }

        setConfig(await getPairsConfig(riskProfileId, true));
        toast.success(resp.msg);
        onCopied?.();
      })
      .catch(packetErrorHandler)
      .finally(() => setCopying(false));
  };

  const instrumentChangeHandler = (
    insId: string,
    rollType: "sourceRoll",
    name: string,
    value: any,
  ) => {
    setConfig((prevConfig) => {
      const clone = [...prevConfig.baseInstruments];
      const targetIndex = clone.findIndex((f) => f.instrumentId === insId);
      if (targetIndex === -1) return prevConfig;

      const instrument = clone[targetIndex];
      clone[targetIndex] = {
        ...instrument,
        [rollType]: {
          ...instrument[rollType],
          [name]: value,
        },
      };

      return { ...prevConfig, baseInstruments: clone };
    });
  };
  return (
    <div className="w-full flex flex-col justify-start items-center gap-5">
      {loading ? <FullScreenLoading /> : <></>}
      <div className="flex w-full justify-evenly text-center">کال ورود</div>
      <SelectableButtons
        label="ریسک پروفایل"
        name="riskProfileId"
        onChange={(_: string, value: string) => setRiskProfileIds(value)}
        options={riskProfiles}
        value={riskProfileId}
        hideSelectAll
      />
      <div
        className="w-full grid grid-cols-5 gap-x-1 gap-y-1 text-xs text-center"
        style={{
          gridTemplateColumns: "40px 1fr 1fr 1fr 1fr",
        }}
      >
        <div />
        <div>مرز DTM</div>
        <div>حداقل ٪ ITM در کمتر از مرز</div>
        <div>حداقل دلتا در بیشتر از مرز</div>
        <div>حداقل ٪ بازده روزانه</div>

        {config && config.baseInstruments && config.baseInstruments.length ? (
          config.baseInstruments.map((ins) => (
            <>
              <div>{ins.instrumentName}</div>
              <TextInput
                label="مرز DTM"
                name="dtmPoint"
                value={ins?.sourceRoll?.dtmPoint?.toString()}
                onChange={(...props) =>
                  instrumentChangeHandler(
                    ins.instrumentId,
                    "sourceRoll",
                    ...props,
                  )
                }
              />
              <TextInput
                label="حداقل ٪ ITM در کمتر از مرز"
                name="minITMPercentage_OverDTM"
                value={ins?.sourceRoll?.minITMPercentage_OverDTM?.toString()}
                onChange={(...props) =>
                  instrumentChangeHandler(
                    ins.instrumentId,
                    "sourceRoll",
                    ...props,
                  )
                }
              />
              <TextInput
                label="حداقل دلتا در بیشتر از مرز"
                name="minDeltaPercentage_UnderDTM"
                value={ins?.sourceRoll?.minDeltaPercentage_UnderDTM?.toString()}
                onChange={(...props) =>
                  instrumentChangeHandler(
                    ins.instrumentId,
                    "sourceRoll",
                    ...props,
                  )
                }
              />
              <TextInput
                label="حداقل ٪ بازده روزانه"
                name="minProfitPercentage"
                value={ins?.sourceRoll?.minProfitPercentage?.toString()}
                onChange={(...props) =>
                  instrumentChangeHandler(
                    ins.instrumentId,
                    "sourceRoll",
                    ...props,
                  )
                }
              />
            </>
          ))
        ) : (
          <></>
        )}
      </div>
      <div className="flex w-full justify-end gap-3">
        {useFixedBasePrices ? (
          <div className="w-[200px]">
            <Button
              onClick={copyFromEntryPosition}
              text="کپی از ورود به پوزیشن"
              variant="outlined"
              loading={copying}
              disabled={loading || copying}
            />
          </div>
        ) : null}
        <div className="w-[100px]">
          <Button
            onClick={setConfigHandler}
            text="ثبت"
            variant="contained"
            disabled={loading || copying}
          />
        </div>
      </div>
    </div>
  );
};

export default OpenningSettingsModal;
